import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { ButtonGroup, ToggleButton } from '../ui/Button';
import { BagService, InventoryService } from '../../services/api';
import axios from '../../services/api';

/**
 * CurrentGearInput - Capture user's current club before showing recommendations
 *
 * Two input modes:
 * - "From My Bag" → Dropdown from GET /bag
 * - "From Inventory" → Searchable dropdown from GET /inventory?category={category}
 *
 * @param {Object} props
 * @param {string} props.category - Driver | Fairway | Hybrid | Iron
 * @param {React.Ref} ref - Ref to expose getGearData() method
 */
const CurrentGearInput = forwardRef(({ category = 'Driver' }, ref) => {
  const [inputMode, setInputMode] = useState('bag'); // 'bag' | 'inventory'
  const [bagClubs, setBagClubs] = useState([]);
  const [inventoryHeads, setInventoryHeads] = useState([]);
  const [inventoryShafts, setInventoryShafts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Golf ball state
  const [golfBalls, setGolfBalls] = useState([]);
  const [userBalls, setUserBalls] = useState([]);
  const [selectedBallId, setSelectedBallId] = useState(null);

  // Form state
  const [selectedBagClub, setSelectedBagClub] = useState('');
  const [selectedInventoryHead, setSelectedInventoryHead] = useState('');
  const [selectedInventoryShaft, setSelectedInventoryShaft] = useState('');
  const [loftDegrees, setLoftDegrees] = useState(10.5);
  const [loftAdjustment, setLoftAdjustment] = useState(0); // Adjustable hosel setting
  const [shaftLength, setShaftLength] = useState('standard');
  const [tradeInValue, setTradeInValue] = useState(0);

  // Load golf balls on mount and set default to Generic Distance Ball
  useEffect(() => {
    const fetchBalls = async () => {
      try {
        // Load inventory balls
        const invResponse = await InventoryService.getBalls();
        const balls = invResponse.balls || [];
        setGolfBalls(balls);

        // Load user's balls from bag
        const bagResponse = await BagService.getBalls();
        const bagBalls = bagResponse.balls || [];
        setUserBalls(bagBalls);

        // Default to Generic Distance Ball (id: 11) or first ball if available
        const defaultBall = balls.find(b => b.brand === 'Generic' && b.model === 'Distance Ball');
        if (defaultBall) {
          setSelectedBallId(defaultBall.id);
        } else if (balls.length > 0) {
          setSelectedBallId(balls[0].id);
        }
      } catch (err) {
        console.error('Failed to load golf balls:', err);
      }
    };
    fetchBalls();
  }, []);

  // Load data based on input mode
  useEffect(() => {
    if (inputMode === 'bag') {
      loadBagClubs();
    } else if (inputMode === 'inventory') {
      loadInventoryHeads();
      loadInventoryShafts();
    }
  }, [inputMode, category]);

  const loadBagClubs = async () => {
    setLoading(true);
    try {
      const clubs = await BagService.getBag();
      // Filter by category
      const filtered = clubs.filter(c => c.category === category);
      setBagClubs(filtered);
      if (filtered.length > 0) {
        setSelectedBagClub(filtered[0].id);
        // Pre-fill loft if available
        if (filtered[0].loft) {
          setLoftDegrees(filtered[0].loft);
        }
      }
    } catch (error) {
      console.error('Failed to load bag clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryHeads = async () => {
    setLoading(true);
    try {
      const data = await InventoryService.getHeads(category);
      setInventoryHeads(data.items || []);
      if (data.items && data.items.length > 0) {
        setSelectedInventoryHead(data.items[0].id);
        setLoftDegrees(data.items[0].loft || 10.5);
      }
    } catch (error) {
      console.error('Failed to load inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryShafts = async () => {
    try {
      const data = await InventoryService.getShafts(category);
      setInventoryShafts(data.items || []);
      if (data.items && data.items.length > 0) {
        setSelectedInventoryShaft(data.items[0].id);
      }
    } catch (error) {
      console.error('Failed to load shafts:', error);
    }
  };

  // Get selected club for loft suggestion
  const getSelectedClubLoft = () => {
    if (inputMode === 'bag') {
      const club = bagClubs.find(c => c.id === parseInt(selectedBagClub));
      return club?.loft;
    } else if (inputMode === 'inventory') {
      const head = inventoryHeads.find(h => h.id === parseInt(selectedInventoryHead));
      return head?.loft;
    }
    return null;
  };

  // Update loft and ball when selection changes
  useEffect(() => {
    const suggestedLoft = getSelectedClubLoft();
    if (suggestedLoft) {
      setLoftDegrees(suggestedLoft);
    }

    // When using bag mode, use the ball from the selected club
    if (inputMode === 'bag') {
      const club = bagClubs.find(c => c.id === parseInt(selectedBagClub));
      if (club?.ball_id) {
        setSelectedBallId(club.ball_id);
      }
    }
  }, [selectedBagClub, selectedInventoryHead, inputMode, bagClubs]);

  // Expose method to parent to get current gear data
  useImperativeHandle(ref, () => ({
    getGearData: () => {
      // Parse and validate trade-in value
      const parsedTradeIn = parseFloat(tradeInValue);
      const validTradeIn = !isNaN(parsedTradeIn) && isFinite(parsedTradeIn) && parsedTradeIn >= 0
        ? parsedTradeIn
        : 0;

      let gear = {
        loftDegrees: parseFloat(loftDegrees),
        loftAdjustment: parseFloat(loftAdjustment), // Adjustable hosel setting
        shaftLength,
        category,
        ballId: selectedBallId,  // Include selected golf ball
        estimatedValue: validTradeIn  // Trade-in value for ROI calc
      };

      if (inputMode === 'bag') {
        const club = bagClubs.find(c => c.id === parseInt(selectedBagClub));
        if (!club) return null;
        gear.clubModel = club?.club_name || 'Unknown';
        gear.clubId = club?.id;
        gear.productId = club?.head_id;
        gear.shaftId = club?.shaft_id;
        // If bag club has shaft data, include it
        if (club?.shaft_id) {
          gear.shaftModel = 'From Bag'; // We don't have shaft details in bag clubs currently
        }
      } else if (inputMode === 'inventory') {
        const head = inventoryHeads.find(h => h.id === parseInt(selectedInventoryHead));
        const shaft = inventoryShafts.find(s => s.id === parseInt(selectedInventoryShaft));
        if (!head) return null;
        gear.clubModel = head ? `${head.manufacturer} ${head.model_name}` : 'Unknown';
        gear.productId = head?.id;
        gear.shaftId = shaft?.id;
        gear.shaftModel = shaft ? `${shaft.manufacturer} ${shaft.model_name}` : 'Unknown';
        gear.shaftFlex = shaft?.flex;
        gear.shaftWeight = shaft?.weight_g;
        if (head?.adjustability) {
          gear.adjustability = head.adjustability;
        } else {
          gear.adjustability = { loft_range: [-1.5, 0, 1.5] };
        }
      }

      return gear;
    }
  }));

  return (
    <div className="space-y-6 pt-4">
        {/* Input Mode Selector */}
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            How would you like to input your club?
          </label>
          <ButtonGroup className="w-full grid grid-cols-2">
            <ToggleButton
              active={inputMode === 'bag'}
              onClick={() => setInputMode('bag')}
              type="button"
            >
              My Bag
            </ToggleButton>
            <ToggleButton
              active={inputMode === 'inventory'}
              onClick={() => setInputMode('inventory')}
              type="button"
            >
              Inventory
            </ToggleButton>
          </ButtonGroup>
        </div>

        {/* Club Selector */}
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            Select Your Club
          </label>

          {inputMode === 'bag' && (
            <>
              {loading ? (
                <div className="text-zinc-500 text-sm">Loading your bag...</div>
              ) : bagClubs.length === 0 ? (
                <div className="text-zinc-500 text-sm">
                  No {category}s in your bag. Try another input mode.
                </div>
              ) : (
                <select
                  value={selectedBagClub}
                  onChange={(e) => setSelectedBagClub(e.target.value)}
                  className="w-full bg-subtle border border-subtle rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  style={{ colorScheme: 'dark' }}
                  required
                >
                  {bagClubs.map(club => (
                    <option key={club.id} value={club.id} className="bg-zinc-900 text-white">
                      {club.club_name} {club.loft ? `(${club.loft}°)` : ''} {club.dexterity ? `[${club.dexterity}]` : ''}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}

          {inputMode === 'inventory' && (
            <>
              {loading ? (
                <div className="text-zinc-500 text-sm">Loading inventory...</div>
              ) : (
                <select
                  value={selectedInventoryHead}
                  onChange={(e) => setSelectedInventoryHead(e.target.value)}
                  className="w-full bg-subtle border border-subtle rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  style={{ colorScheme: 'dark' }}
                  required
                >
                  {inventoryHeads.slice(0, 50).map(head => (
                    <option key={head.id} value={head.id} className="bg-zinc-900 text-white">
                      {head.manufacturer} {head.model_name} ({head.loft}°) [{head.dexterity || 'RH'}]
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
        </div>

        {/* Shaft Selector */}
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            Select Your Shaft
          </label>

          {inputMode === 'bag' && (
            <div className="text-zinc-500 text-sm">
              Shaft loaded from your bag
            </div>
          )}

          {inputMode === 'inventory' && (
            <>
              {loading ? (
                <div className="text-zinc-500 text-sm">Loading shafts...</div>
              ) : inventoryShafts.length === 0 ? (
                <div className="text-zinc-500 text-sm">
                  No {category} shafts in inventory.
                </div>
              ) : (
                <select
                  value={selectedInventoryShaft}
                  onChange={(e) => setSelectedInventoryShaft(e.target.value)}
                  className="w-full bg-subtle border border-subtle rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  style={{ colorScheme: 'dark' }}
                  required
                >
                  {inventoryShafts.slice(0, 50).map(shaft => (
                    <option key={shaft.id} value={shaft.id} className="bg-zinc-900 text-white">
                      {shaft.manufacturer} {shaft.model_name} ({shaft.flex}, {shaft.weight_g}g)
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
        </div>

        {/* Golf Ball Selector */}
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            Golf Ball
            <span className="ml-2 text-xs text-zinc-500">
              {inputMode === 'bag' ? 'From your bag' : 'Affects spin & trajectory'}
            </span>
          </label>
          <select
            value={selectedBallId || ''}
            onChange={(e) => {
              const newBallId = e.target.value ? parseInt(e.target.value) : null;
              setSelectedBallId(newBallId);
              console.log('Ball selected:', newBallId);
            }}
            disabled={inputMode === 'bag'}
            className={`w-full bg-subtle border border-subtle rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-primary ${
              inputMode === 'bag' ? 'opacity-60 cursor-not-allowed' : ''
            }`}
            style={{ colorScheme: 'dark' }}
          >
            <option value="">No Ball Selected</option>
            {golfBalls.map(ball => (
              <option key={ball.id} value={ball.id} className="bg-zinc-900 text-white">
                {ball.display_name} - {ball.driver_spin_profile} spin ({ball.feel} feel)
              </option>
            ))}
          </select>
          {inputMode === 'bag' && (
            <div className="mt-2 text-xs text-zinc-500">
              Ball is pulled from the selected club in your bag. Edit your bag to change the ball.
            </div>
          )}
          {inputMode !== 'bag' && selectedBallId && golfBalls.find(b => b.id === selectedBallId) && (
            <div className="mt-2 text-xs text-zinc-500">
              {golfBalls.find(b => b.id === selectedBallId).notes}
            </div>
          )}
        </div>

        {/* Club Settings - Compact Grid */}
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            Current Settings
          </label>
          <div className="grid grid-cols-3 gap-3">
            {/* Loft */}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Loft</label>
              <input
                type="number"
                step="0.5"
                value={loftDegrees}
                onChange={(e) => setLoftDegrees(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="10.5°"
                required
              />
            </div>

            {/* Loft Adjustment (Adjustable Hosel) */}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Loft Adj.</label>
              <select
                value={loftAdjustment}
                onChange={(e) => setLoftAdjustment(parseFloat(e.target.value))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                style={{ colorScheme: 'dark' }}
                title="Adjustable hosel loft setting"
              >
                <option value={-2} className="bg-zinc-900 text-white">-2.0°</option>
                <option value={-1.5} className="bg-zinc-900 text-white">-1.5°</option>
                <option value={0} className="bg-zinc-900 text-white">STD</option>
                <option value={1.5} className="bg-zinc-900 text-white">+1.5°</option>
                <option value={2} className="bg-zinc-900 text-white">+2.0°</option>
              </select>
            </div>

            {/* Lie */}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Lie</label>
              <select
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                style={{ colorScheme: 'dark' }}
              >
                <option value="standard" className="bg-zinc-900 text-white">Standard</option>
                <option value="upright" className="bg-zinc-900 text-white">Upright (+2°)</option>
                <option value="flat" className="bg-zinc-900 text-white">Flat (-2°)</option>
              </select>
            </div>

            {/* Bias */}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Bias</label>
              <select
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                style={{ colorScheme: 'dark' }}
              >
                <option value="neutral" className="bg-zinc-900 text-white">Neutral</option>
                <option value="draw" className="bg-zinc-900 text-white">Draw</option>
                <option value="fade" className="bg-zinc-900 text-white">Fade</option>
              </select>
            </div>
          </div>

          {/* Trade-In Value (for ROI Calculator) */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Estimated Trade-In Value
              <span className="ml-2 text-xs text-zinc-500">(Optional - for ROI analysis)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-zinc-500">$</span>
              <input
                type="number"
                value={tradeInValue}
                onChange={(e) => setTradeInValue(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="0"
                min="0"
                step="50"
              />
            </div>
            <p className="text-xs text-zinc-600 mt-1">
              Estimate what you could sell/trade your current club for
            </p>
          </div>
        </div>

        {/* Shaft Length - Club Agnostic */}
        <div>
          <label className="block text-sm font-medium text-zinc-400 mb-2">
            Shaft Length
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'shorter', label: 'Shorter' },
              { value: 'standard', label: 'Standard' },
              { value: 'longer', label: 'Longer' }
            ].map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setShaftLength(option.value)}
                className={`p-3 rounded-lg border transition-colors ${
                  shaftLength === option.value
                    ? 'bg-primary border-primary text-white'
                    : 'bg-subtle border-subtle text-zinc-300 hover:border-zinc-600'
                }`}
              >
                <div className="font-medium text-sm">{option.label}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Relative to standard length for your club type
          </p>
        </div>

      </div>
  );
});

CurrentGearInput.displayName = 'CurrentGearInput';

export default CurrentGearInput;
